import { makeAutoObservable, observable, runInAction } from 'mobx'

/* eslint-disable-next-line import-x/no-restricted-paths -- FIXME: UXP-4500 */
import type { StepDefinitionId } from '@src/app/components/workflow/types'

import type Service from '.'
import PersistedCollection from './collections/PersistedCollection'
import type { RawWorkflowStepDefinition } from './model/workflow/WorkflowStepDefinitionModel'
import WorkflowStepDefinitionModel from './model/workflow/WorkflowStepDefinitionModel'
import type { WorkflowStepDefinitionRepository } from './worker/repository/WorkflowStepDefinitionRepository'
import { WORKFLOW_STEP_DEFINITION_TABLE_NAME } from './worker/repository/WorkflowStepDefinitionRepository'

export default class WorkflowStepDefinitionStore {
  private readonly stepDefinitionsCollection: PersistedCollection<
    WorkflowStepDefinitionModel,
    WorkflowStepDefinitionRepository
  >
  // We keep track of the step definition ids for which we have fetched definitions
  private readonly fetchedStepDefinitionIdsAndVersions: [StepDefinitionId, number][] = []

  constructor(private root: Service) {
    this.stepDefinitionsCollection = new PersistedCollection({
      table: root.storage.table(WORKFLOW_STEP_DEFINITION_TABLE_NAME),
      classConstructor: (json: RawWorkflowStepDefinition) =>
        new WorkflowStepDefinitionModel(json),
    })

    makeAutoObservable<this, 'stepDefinitionsCollection'>(this, {
      stepDefinitionsCollection: observable,
    })

    void this.load()
  }

  getStepDefinitionById(
    stepDefinitionId: StepDefinitionId,
    version?: number,
  ): WorkflowStepDefinitionModel | null {
    const localStepDefinition = this.stepDefinitionsCollection.get(stepDefinitionId, {
      version,
    })

    if (!localStepDefinition && version) {
      // If the step definition is not found locally, fetch it from remote. This assumes that we already fetched the list
      // of the latest step definitions from the remote. If so, this will be called only if the workflow has some older
      // step definitions versions.
      void this.fetchStepDefinitionFromRemoteIfNeeded(
        stepDefinitionId,
        localStepDefinition,
        version,
      )
    }

    return localStepDefinition ?? null
  }

  getLatestVersionByStepDefinitionId(
    stepDefinitionId: StepDefinitionId,
  ): WorkflowStepDefinitionModel | null {
    return this.stepDefinitionsCollection.getLatestVersion(stepDefinitionId) ?? null
  }

  getLatestVersionNumberByStepDefinitionId(stepDefinitionId: StepDefinitionId): number {
    return this.stepDefinitionsCollection.getLatestVersion(stepDefinitionId)?.version ?? 1
  }

  /**
   * Fetches the latest step definitions from the remote and adds them to the local collection. If an array of step definitions
   * and versions is provided as a parameter, it will fetch only the step definitions that are not in the local collection
   * (in other words, the step definitions that are older than the ones in the local collection).
   *
   * @param stepDefinitionIds - The list of step definitions and versions to fetch, if they haven't been fetched when fetching the latest step definitions.
   */
  fetchStepDefinitions(stepDefinitionIds: [StepDefinitionId, number][]) {
    void this.root.transport.workflow.stepDefinitions.list(1000).then((response) => {
      const fetchedStepDefinitions = response.data.map(
        (item) => new WorkflowStepDefinitionModel(item),
      )
      this.stepDefinitionsCollection.putBulk(fetchedStepDefinitions)
      this.fetchedStepDefinitionIdsAndVersions.push(
        ...fetchedStepDefinitions.map(
          (item) => [item.id, item.version] as [StepDefinitionId, number],
        ),
      )

      this.fetchOlderStepDefinitionsIfNeeded(stepDefinitionIds)
    })
  }

  /**
   * Fetches the step definitions that are not in the local collection and are older than the ones in the local collection.
   *
   * @param olderStepDefinitionIds - The list of step definitions and versions to fetch, if they haven't been fetched when fetching the latest step definitions.
   */
  private fetchOlderStepDefinitionsIfNeeded(
    olderStepDefinitionIds: [StepDefinitionId, number][],
  ) {
    const stepDefinitionsToFetch = olderStepDefinitionIds.filter(
      ([id, version]) =>
        !this.fetchedStepDefinitionIdsAndVersions.some(
          ([id2, version2]) => id2 === id && version2 === version,
        ),
    )

    void Promise.all(
      stepDefinitionsToFetch.map(([id, version]) =>
        this.fetchStepDefinitionFromRemoteIfNeeded(id, null, version),
      ),
    )
  }

  private async fetchStepDefinitionFromRemoteIfNeeded(
    stepDefinitionId: StepDefinitionId,
    localStepDefinition: WorkflowStepDefinitionModel | null,
    version: number,
  ) {
    // We want to fetch the remote step definition at least once per session
    // to make sure we have the latest data
    const alreadyFetched = this.fetchedStepDefinitionIdsAndVersions.some(
      ([id, v]) => id === stepDefinitionId && v === version,
    )
    if (alreadyFetched) {
      return
    }

    runInAction(() => {
      this.fetchedStepDefinitionIdsAndVersions.push([stepDefinitionId, version])
    })
    const remoteStepDefinition = await this.fetchStepDefinitionById(
      stepDefinitionId,
      version,
    )

    if (localStepDefinition && !remoteStepDefinition) {
      // If the remote step definition has been deleted, delete the local one too
      this.stepDefinitionsCollection.delete(
        localStepDefinition.id,
        localStepDefinition.version,
      )
    } else if (localStepDefinition && remoteStepDefinition) {
      // If both local and remote exist, update local step definition with remote data
      localStepDefinition.deserialize(remoteStepDefinition)
    } else if (!localStepDefinition && remoteStepDefinition) {
      // If the step definition exists only on remote, add it to the local collection
      this.stepDefinitionsCollection.put(
        new WorkflowStepDefinitionModel(remoteStepDefinition),
      )
    }
  }

  private async fetchStepDefinitionById(id: string, version: number) {
    const response = await this.root.transport.workflow.stepDefinitions.get(id, version)

    if (!response) {
      return
    }

    return response
  }

  private load() {
    return this.stepDefinitionsCollection.performQuery((repo) => repo.all())
  }
}
